import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { C, S, BASE } from "../constants";
import Img from "./Img";
import ProdLink from "./ProdLink";

const PRIORITIES = [
  { v: "low",      l: "Низький",    color: C.text3 },
  { v: "medium",   l: "Середній",   color: C.blue },
  { v: "high",     l: "Високий",    color: C.yellow },
  { v: "critical", l: "Критичний",  color: C.pink },
];

const STATUSES = [
  { v: "todo",        l: "Нові" },
  { v: "in_progress", l: "В роботі" },
  { v: "review",      l: "На перевірці" },
  { v: "done",        l: "Виконано" },
];

function MemberAvatar({ member, size = 24 }) {
  if (!member) return null;
  const initials = member.name.trim().split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: member.color || "#6366f1",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.38, fontWeight: 700, color: "#fff", flexShrink: 0,
    }}>{initials}</div>
  );
}

export { MemberAvatar };

function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("uk-UA", { day: "numeric", month: "short" }) +
    " " + d.toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit" });
}

function isImage(mime) {
  return mime && mime.startsWith("image/");
}

export default function TaskModal({ task, members, onSave, onDelete, onClose, token }) {
  const isEdit = !!task?.id;

  const [title, setTitle]         = useState(task?.title || "");
  const [desc, setDesc]           = useState(task?.description || "");
  const [priority, setPriority]   = useState(task?.priority || "medium");
  const [status, setStatus]       = useState(task?.status || "todo");
  const [assigneeId, setAssigneeId] = useState(task?.assignee_id || null);
  const [dueDate, setDueDate]     = useState(task?.due_date || "");
  const [productId, setProductId] = useState(task?.product_rozetka_id || null);
  const [productName, setProductName] = useState(task?.product_name || "");
  const [productImage, setProductImage] = useState(task?.product_image || "");
  const [productSearch, setProductSearch] = useState(task?.product_name || "");
  const [productResults, setProductResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving]       = useState(false);
  const searchTimer = useRef(null);

  // Comments & attachments
  const [comments, setComments]       = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentAuthor, setCommentAuthor] = useState(
    () => localStorage.getItem("crm_comment_author") || ""
  );
  const [commentSaving, setCommentSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const authHeaders = { Authorization: "Bearer " + token };

  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Load comments & attachments for existing tasks
  useEffect(() => {
    if (!isEdit) return;
    setActivityLoading(true);
    fetch(`${BASE}/api/tasks/${task.id}/comments`, { headers: authHeaders })
      .then(r => r.json())
      .then(d => {
        setComments(d.comments || []);
        setAttachments(d.attachments || []);
      })
      .catch(() => {})
      .finally(() => setActivityLoading(false));
  }, [task?.id]); // eslint-disable-line

  // Product search
  useEffect(() => {
    clearTimeout(searchTimer.current);
    if (!productSearch.trim() || productSearch === productName) {
      setProductResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await fetch(`${BASE}/api/products?days=0&show_inactive=false`, {
          headers: authHeaders,
        });
        const data = await r.json();
        const q = productSearch.toLowerCase();
        const filtered = (data.products || [])
          .filter(p => p.name?.toLowerCase().includes(q) || p.article?.toLowerCase().includes(q))
          .slice(0, 8);
        setProductResults(filtered);
      } catch {}
      setSearching(false);
    }, 350);
  }, [productSearch]); // eslint-disable-line

  function selectProduct(p) {
    setProductId(p.rozetka_id);
    setProductName(p.name);
    setProductImage(p.image_url || "");
    setProductSearch(p.name);
    setProductResults([]);
  }

  function clearProduct() {
    setProductId(null);
    setProductName("");
    setProductImage("");
    setProductSearch("");
    setProductResults([]);
  }

  async function handleSave() {
    if (!title.trim()) return;
    setSaving(true);
    await onSave({
      title: title.trim(),
      description: desc,
      priority,
      status,
      assignee_id: assigneeId || null,
      due_date: dueDate || null,
      product_rozetka_id: productId || null,
      product_name: productName || null,
      product_image: productImage || null,
    });
    setSaving(false);
  }

  async function handleAddComment() {
    const text = commentText.trim();
    if (!text) return;
    setCommentSaving(true);
    localStorage.setItem("crm_comment_author", commentAuthor);
    try {
      const r = await fetch(`${BASE}/api/tasks/${task.id}/comments`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ text, author_name: commentAuthor.trim() || null }),
      });
      const c = await r.json();
      setComments(prev => [...prev, c]);
      setCommentText("");
    } catch {}
    setCommentSaving(false);
  }

  async function handleDeleteComment(id) {
    await fetch(`${BASE}/api/tasks/${task.id}/comments/${id}`, {
      method: "DELETE", headers: authHeaders,
    });
    setComments(prev => prev.filter(c => c.id !== id));
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await fetch(`${BASE}/api/tasks/${task.id}/attachments`, {
        method: "POST",
        headers: authHeaders,
        body: form,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        alert(err.detail || "Помилка завантаження");
      } else {
        const a = await r.json();
        setAttachments(prev => [...prev, a]);
      }
    } catch {}
    setUploading(false);
    e.target.value = "";
  }

  async function handleDeleteAttachment(id) {
    await fetch(`${BASE}/api/tasks/${task.id}/attachments/${id}`, {
      method: "DELETE", headers: authHeaders,
    });
    setAttachments(prev => prev.filter(a => a.id !== id));
  }

  async function handleDownload(att) {
    try {
      const r = await fetch(`${BASE}/api/files/${att.filename}`, { headers: authHeaders });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  }

  const priorityColor = PRIORITIES.find(p => p.v === priority)?.color || C.blue;

  return createPortal(
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 3000,
        background: "rgba(0,0,0,.7)", display: "flex",
        alignItems: "center", justifyContent: "center", padding: 20,
      }}
    >
      <div style={{
        background: C.bg2, border: `1px solid ${C.border}`,
        borderRadius: 16, width: "100%", maxWidth: 600,
        maxHeight: "92vh", overflowY: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,.8)",
      }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px", borderBottom: `1px solid ${C.border}`,
          position: "sticky", top: 0, background: C.bg2, zIndex: 1,
        }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: C.text1 }}>
            {isEdit ? "Редагувати задачу" : "Нова задача"}
          </span>
          <button onClick={onClose} style={{
            background: "none", border: "none", color: C.text3,
            fontSize: 18, cursor: "pointer", lineHeight: 1,
          }}>✕</button>
        </div>

        <div style={{ padding: "20px 20px 24px" }}>
          {/* Title */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: C.text3, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Заголовок *</div>
            <input
              autoFocus
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Що потрібно зробити?"
              style={{ ...S.input, width: "100%", boxSizing: "border-box", fontSize: 14 }}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: C.text3, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Опис</div>
            <textarea
              value={desc}
              onChange={e => setDesc(e.target.value)}
              placeholder="Деталі задачі..."
              rows={3}
              style={{ ...S.input, width: "100%", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }}
            />
          </div>

          {/* Priority + Status row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: C.text3, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Пріоритет</div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {PRIORITIES.map(p => (
                  <button key={p.v} onClick={() => setPriority(p.v)} style={{
                    padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                    cursor: "pointer", border: `1px solid ${priority === p.v ? p.color + "80" : C.border}`,
                    background: priority === p.v ? p.color + "20" : "transparent",
                    color: priority === p.v ? p.color : C.text3,
                  }}>{p.l}</button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.text3, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Статус</div>
              <select
                value={status}
                onChange={e => setStatus(e.target.value)}
                style={{ ...S.input, width: "100%" }}
              >
                {STATUSES.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </div>
          </div>

          {/* Assignee + Due date row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: C.text3, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Виконавець</div>
              <select
                value={assigneeId || ""}
                onChange={e => setAssigneeId(e.target.value ? Number(e.target.value) : null)}
                style={{ ...S.input, width: "100%" }}
              >
                <option value="">— Не призначено —</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.text3, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Дедлайн</div>
              <input
                type="date"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                style={{ ...S.input, width: "100%", boxSizing: "border-box", colorScheme: "dark" }}
              />
            </div>
          </div>

          {/* Product link */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: C.text3, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Товар</div>
            {productId ? (
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                background: C.bg3, borderRadius: 8, padding: "8px 12px",
                border: `1px solid ${C.border}`,
              }}>
                {productImage && <Img src={productImage} size={32} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <ProdLink name={productName} productId={productId} style={{ fontSize: 12, fontWeight: 400 }} />
                </div>
                <button onClick={clearProduct} style={{
                  background: "none", border: "none", color: C.text3,
                  fontSize: 14, cursor: "pointer", flexShrink: 0,
                }}>✕</button>
              </div>
            ) : (
              <div style={{ position: "relative" }}>
                <input
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  placeholder="Пошук товару за назвою або артикулом..."
                  style={{ ...S.input, width: "100%", boxSizing: "border-box" }}
                />
                {searching && (
                  <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: C.text3, fontSize: 11 }}>...</div>
                )}
                {productResults.length > 0 && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
                    background: C.bg3, border: `1px solid ${C.border}`, borderRadius: 8,
                    marginTop: 2, boxShadow: "0 8px 24px rgba(0,0,0,.6)", maxHeight: 240, overflowY: "auto",
                  }}>
                    {productResults.map(p => (
                      <div key={p.rozetka_id} onClick={() => selectProduct(p)} style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "8px 12px", cursor: "pointer",
                        borderBottom: `1px solid ${C.border}`,
                      }} onMouseEnter={e => e.currentTarget.style.background = C.bg2}
                         onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <Img src={p.image_url} size={28} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12, color: C.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                          {p.article && <div style={{ fontSize: 10, color: C.text3 }}>{p.article}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isEdit ? 28 : 0 }}>
            <div>
              {isEdit && onDelete && (
                <button onClick={() => onDelete(task.id)} style={{
                  ...S.input, padding: "8px 14px", cursor: "pointer",
                  color: C.pink, fontSize: 12, fontWeight: 600,
                  background: C.pink + "12", border: `1px solid ${C.pink}40`,
                }}>Видалити</button>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={onClose} style={{
                ...S.input, padding: "8px 16px", cursor: "pointer", fontSize: 12,
              }}>Скасувати</button>
              <button
                onClick={handleSave}
                disabled={!title.trim() || saving}
                style={{
                  padding: "8px 18px", borderRadius: 9, fontSize: 13, fontWeight: 700,
                  cursor: title.trim() && !saving ? "pointer" : "default",
                  background: title.trim() && !saving ? C.blue : C.border,
                  color: title.trim() && !saving ? "#fff" : C.text3,
                  border: "none",
                }}
              >{saving ? "Збереження..." : isEdit ? "Зберегти" : "Створити"}</button>
            </div>
          </div>

          {/* ── Comments & Attachments (edit mode only) ── */}
          {isEdit && (
            <>
              {/* Attachments */}
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 20, marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.text2, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Файли {attachments.length > 0 && <span style={{ color: C.text3, fontWeight: 400 }}>({attachments.length})</span>}
                  </span>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    style={{
                      padding: "4px 12px", borderRadius: 7, fontSize: 11, fontWeight: 600,
                      border: `1px solid ${C.border}`, background: "transparent",
                      color: C.text2, cursor: "pointer",
                    }}
                  >{uploading ? "Завантаження..." : "+ Додати файл"}</button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleUpload}
                  />
                </div>

                {activityLoading && attachments.length === 0 && (
                  <div style={{ fontSize: 11, color: C.text3, padding: "8px 0" }}>Завантаження...</div>
                )}

                {attachments.map(att => (
                  <div key={att.id} style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "7px 10px", background: C.bg3, borderRadius: 8,
                    marginBottom: 6, border: `1px solid ${C.border}`,
                  }}>
                    <span style={{ fontSize: 18, flexShrink: 0 }}>
                      {isImage(att.mime_type) ? "🖼" : att.mime_type?.includes("pdf") ? "📄" : "📎"}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: C.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {att.original_name}
                      </div>
                      <div style={{ fontSize: 10, color: C.text3 }}>
                        {fmtSize(att.file_size)}{att.created_at ? " · " + fmtTime(att.created_at) : ""}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownload(att)}
                      title="Завантажити"
                      style={{ background: "none", border: "none", color: C.blue, fontSize: 13, cursor: "pointer", padding: "2px 4px" }}
                    >↓</button>
                    <button
                      onClick={() => handleDeleteAttachment(att.id)}
                      title="Видалити"
                      style={{ background: "none", border: "none", color: C.text3, fontSize: 13, cursor: "pointer", padding: "2px 4px" }}
                    >✕</button>
                  </div>
                ))}

                {!activityLoading && attachments.length === 0 && (
                  <div style={{ fontSize: 11, color: C.text3, padding: "4px 0" }}>Немає прикріплених файлів</div>
                )}
              </div>

              {/* Comments */}
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text2, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 14 }}>
                  Коментарі {comments.length > 0 && <span style={{ color: C.text3, fontWeight: 400 }}>({comments.length})</span>}
                </div>

                {comments.map(c => (
                  <div key={c.id} style={{
                    marginBottom: 10, padding: "10px 12px",
                    background: C.bg3, borderRadius: 8, border: `1px solid ${C.border}`,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 5 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: C.text2 }}>
                        {c.author_name || "Анонім"}
                      </span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 10, color: C.text3 }}>{fmtTime(c.created_at)}</span>
                        <button
                          onClick={() => handleDeleteComment(c.id)}
                          style={{ background: "none", border: "none", color: C.text3, fontSize: 12, cursor: "pointer", padding: 0, lineHeight: 1 }}
                        >✕</button>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: C.text1, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{c.text}</div>
                  </div>
                ))}

                {!activityLoading && comments.length === 0 && (
                  <div style={{ fontSize: 11, color: C.text3, marginBottom: 12 }}>Поки немає коментарів</div>
                )}

                {/* Add comment */}
                <div style={{ marginTop: 12 }}>
                  <input
                    value={commentAuthor}
                    onChange={e => setCommentAuthor(e.target.value)}
                    placeholder="Ваше ім'я (необов'язково)"
                    style={{ ...S.input, width: "100%", boxSizing: "border-box", fontSize: 12, marginBottom: 8 }}
                  />
                  <textarea
                    value={commentText}
                    onChange={e => setCommentText(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleAddComment();
                    }}
                    placeholder="Напишіть коментар... (Ctrl+Enter — відправити)"
                    rows={2}
                    style={{ ...S.input, width: "100%", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit", lineHeight: 1.5, marginBottom: 8 }}
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={!commentText.trim() || commentSaving}
                    style={{
                      padding: "7px 16px", borderRadius: 8, fontSize: 12, fontWeight: 700,
                      border: "none", cursor: commentText.trim() ? "pointer" : "default",
                      background: commentText.trim() ? C.blue : C.border,
                      color: commentText.trim() ? "#fff" : C.text3,
                    }}
                  >{commentSaving ? "..." : "Додати коментар"}</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
